import { Component, OnInit, OnDestroy, Inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { catchError, EMPTY, Subject, takeUntil } from "rxjs";
import { UserBridgeService } from "../../api/user-bridge/user-bridge.service";
import { TransferProfileRequest, UserShortInfo } from "../../api/user-bridge/user-bridge.types";
import { NzMessageService } from "ng-zorro-antd/message";
import { TranslateService } from "@ngx-translate/core";
import { Title } from "@angular/platform-browser";
import { LoginService } from "../login-page/login-page-data/login.service";
import { ProfileService } from "../../api/profile/profile.service";
import { CmsCachedService } from "../../api/cms/cms-cached.service";
import { CmsNameValue, compareCmsNameValue } from "../../api/cms/cms.types";
import { Talent } from "../../api/sign-up/signup.types";
import { TalentProfileFactory } from "../../api/profile/talent-profile.factory";
import { Observable } from "rxjs";
import {
  getLegalLinks,
  NumberRequiredValidator,
  SpecialCharacterRequiredValidator,
  UpperCaseRequiredValidator,
} from "@momhunting/core";
import { AppConfig } from "libs/core/src/lib/app-config/config.types";
import { APP_CONFIGURATION } from "libs/core/src/lib/core.di";
export enum WelcomePageStep {
  LOADING = "loading",
  WELCOME = "welcome",
  SET_PASSWORD = "set_password",
  SET_REASON_WHY = "set_reason_why",
  SUCCESS = "success",
  ERROR = "error",
}

@Component({
  selector: "mh-mh-welcome-page",
  templateUrl: "./mh-welcome-page.component.html",
  styleUrls: ["./mh-welcome-page.component.less"],
})
export class MhWelcomePageComponent implements OnInit, OnDestroy {
  currentStep = WelcomePageStep.LOADING;
  WelcomePageStep = WelcomePageStep;

  authToken: string | null = null;
  userInfo: UserShortInfo | null = null;
  newAccountId: string | null = null;
  oldPassword: string | null = null;
  isLoading = false;
  errorMessage = "";

  welcomeForm = new FormGroup({
    acceptTerms: new FormControl(false, [Validators.requiredTrue]),
    acceptNewsletter: new FormControl(false),
  });

  passwordForm = new FormGroup({
    password: new FormControl("", [
      Validators.required,
      Validators.minLength(8),
      NumberRequiredValidator,
      SpecialCharacterRequiredValidator,
      UpperCaseRequiredValidator,
    ]),
  });

  profileInfoForm = new FormGroup({
    reasonWhy: new FormControl(null, [Validators.required]),
    gender: new FormControl(null, [Validators.required]),
  });

  reasonWhyOptions$: Observable<Array<CmsNameValue>>;
  genderOptions$: Observable<Array<CmsNameValue>>;
  termsUrl: string;
  privacyUrl: string;

  private destroy$ = new Subject<void>();

  get password() {
    return this.passwordForm.get("password") as FormControl;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userBridgeService: UserBridgeService,
    private messageService: NzMessageService,
    private translateService: TranslateService,
    private titleService: Title,
    private loginService: LoginService,
    private profileService: ProfileService,
    private cmsCachedService: CmsCachedService,
    private talentProfileFactory: TalentProfileFactory,
    @Inject(APP_CONFIGURATION) appConfig: AppConfig,
  ) {
    const legalLinks = getLegalLinks(appConfig.brand);
    this.termsUrl = legalLinks.termsUrl;
    this.privacyUrl = legalLinks.privacyUrl;
    this.titleService.setTitle(this.translateService.instant("user-bridge.page-title"));
    this.reasonWhyOptions$ = this.cmsCachedService.getUserDataByName("reason-why", false);
    this.genderOptions$ = this.cmsCachedService.getUserDataByName("gender", false);
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.authToken = params["token"];
      if (this.authToken) {
        this.loadUserInfo();
      } else {
        this.handleError(this.translateService.instant("user-bridge.errors.invalid-token"));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUserInfo(): void {
    if (!this.authToken) return;

    this.isLoading = true;
    this.userBridgeService
      .getUserShortInfo(this.authToken)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.handleError(this.translateService.instant("user-bridge.errors.load-user-failed"));
          return EMPTY;
        }),
      )
      .subscribe((userInfo) => {
        this.userInfo = userInfo;
        this.currentStep = WelcomePageStep.WELCOME;
        this.isLoading = false;
      });
  }

  onAcceptTerms(): void {
    if (this.welcomeForm.invalid) {
      this.welcomeForm.markAllAsTouched();
      return;
    }

    if (!this.authToken) {
      this.handleError(this.translateService.instant("user-bridge.errors.missing-token"));
      return;
    }

    this.isLoading = true;

    const newsletterConsent = this.welcomeForm.get("acceptNewsletter")?.value || false;

    const transferRequest: TransferProfileRequest = {
      termsAndConditionsAccepted: true,
      newsletterSubscriptionAccepted: newsletterConsent,
    };

    this.userBridgeService
      .transferProfile(this.authToken, transferRequest)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.handleError(this.translateService.instant("user-bridge.errors.transfer-profile-failed"));
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        if (response.success) {
          this.newAccountId = response.accountId;
          this.oldPassword = response.oldPassword;
          // Save the new authToken for subsequent API calls
          this.loginService.setToken(response.authToken);

          this.currentStep = WelcomePageStep.SET_PASSWORD;
        } else {
          this.handleError(this.translateService.instant("user-bridge.errors.transfer-profile-failed"));
        }
        this.isLoading = false;
      });
  }

  onSetPassword(): void {
    if (this.passwordForm.invalid) {
      this.passwordForm.markAllAsTouched();
      return;
    }

    if (!this.oldPassword) {
      this.handleError(this.translateService.instant("user-bridge.errors.missing-password-data"));
      return;
    }

    const newPassword = this.passwordForm.get("password")?.value;
    if (!newPassword) {
      this.handleError(this.translateService.instant("user-bridge.errors.password-required"));
      return;
    }

    this.isLoading = true;
    this.loginService
      .passwordChange(this.oldPassword, newPassword)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.handleError(this.translateService.instant("user-bridge.errors.set-password-failed"));
          return EMPTY;
        }),
      )
      .subscribe((newAuthToken) => {
        if (newAuthToken) {
          // Save the new authToken after password change
          this.loginService.setToken(newAuthToken);
          this.currentStep = WelcomePageStep.SET_REASON_WHY;
        } else {
          this.handleError(this.translateService.instant("user-bridge.errors.set-password-failed"));
        }
        this.isLoading = false;
      });
  }

  onSetReasonWhy(): void {
    if (this.profileInfoForm.invalid) {
      this.profileInfoForm.markAllAsTouched();
      return;
    }

    const reasonWhy = this.profileInfoForm.get("reasonWhy")?.value;
    if (!reasonWhy) {
      this.handleError(this.translateService.instant("user-bridge.errors.reason-why-required"));
      return;
    }

    const gender = this.profileInfoForm.get("gender")?.value;

    this.isLoading = true;

    this.profileService
      .loadTalentProfile()
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          this.handleError(this.translateService.instant("user-bridge.errors.set-reason-why-failed"));
          return EMPTY;
        }),
      )
      .subscribe((currentProfile) => {
        const talentUpdate: Talent = {
          ...currentProfile,
          reasonWhy: reasonWhy,
          gender: gender || undefined,
        };

        this.profileService
          .talentUpdate(talentUpdate)
          .pipe(
            takeUntil(this.destroy$),
            catchError((error) => {
              this.handleError(this.translateService.instant("user-bridge.errors.set-reason-why-failed"));
              return EMPTY;
            }),
          )
          .subscribe((success) => {
            if (success) {
              this.currentStep = WelcomePageStep.SUCCESS;
            } else {
              this.handleError(this.translateService.instant("user-bridge.errors.set-reason-why-failed"));
            }
            this.isLoading = false;
          });
      });
  }

  private handleError(message: string): void {
    this.errorMessage = message;
    this.currentStep = WelcomePageStep.ERROR;
    this.isLoading = false;
    this.messageService.error(message);
  }

  goToLogin(): void {
    this.router.navigate(["/login"]);
  }

  goToDashboard(): void {
    this.router.navigate(["/dashboard"]);
  }

  protected readonly compareCmsNameValue = compareCmsNameValue;
}
